import React, { useState } from "react";
import { useChair } from "../../../context/ChairContext";
import { ChairFrameOptions, Frame, Texture, useListChairFrameOptions } from "../../../api/hooks/useFrame";
import { Loader } from "../../Loader";
import { useStep } from "../../../context/stepContext";
import { Step } from "./Step";
import { plausibleEvent, toLocalNumber } from "../../../utilities/helper";
import { ChairInfoButton } from "../ChairInfoButton";

type BreadCrumbProps = {
    current: FrameBreadCrumb;
    setCoverStep: (crumb: FrameBreadCrumb) => void;
};

enum FrameBreadCrumb {
    MATERIAL,
    FINISHING,
}

type FrameStepProps = {
    setFrameStep: (breadcrumb: FrameBreadCrumb) => void;
};

type FrameMaterialProps = FrameStepProps & {
    setFinishings: (finishings: Array<Texture>) => void;
};

type MaterialOptionProps = {
    frame: Frame;
    setFinishings: (finishings: Array<Texture>) => void;
    setFrameStep: (breadcrumb: FrameBreadCrumb) => void;
};

type FrameFinishingProps = FrameStepProps & {
    finishings: Array<Texture>;
};

type FinishingOptionProps = {
    finishing: Texture;
};

const BreadCrumbs = (props: BreadCrumbProps) => {
    const crumbs = [
        {
            name: "Material",
            crumb: FrameBreadCrumb.MATERIAL,
        },
        {
            name: "Verarbeitung",
            crumb: FrameBreadCrumb.FINISHING,
        },
    ];
    return (
        <div className={"breadcrumbs"}>
            {crumbs.map((crumb, index) => {
                return (
                    <button
                        key={index}
                        className={`button breadcrumb-button icon-button ${
                            crumb.crumb === props.current ? "current" : ""
                        }`}
                        onClick={() => props.setCoverStep(crumb.crumb)}
                        disabled={crumb.crumb === props.current}
                    >
                        {crumb.name}
                    </button>
                );
            })}
        </div>
    );
};

const FrameMaterialOption = (props: MaterialOptionProps) => {
    const id = useChair((state) => state.chair.id);
    const chairFrame = useChair((state) => state.chair.frame);
    const setChair = useChair((state) => state.setChair);
    const frame = props.frame;
    const frameInfo = frame.frame[0];

    const handleClick = (event) => {
        event.stopPropagation();

        if (chairFrame?.name !== frameInfo.frameName) {
            setChair({
                id: id,
                frame: { id: frameInfo.id, name: frameInfo.frameName, price: frame.frameCost },
            });
        }
        props.setFinishings(frameInfo.textures);
        props.setFrameStep(FrameBreadCrumb.FINISHING);
        plausibleEvent("frame-selected", frameInfo.frameName);
    };

    return (
        <li
            className={`frame-material-option ${frameInfo.frameName === chairFrame?.name ? "current" : ""}`}
            onClick={handleClick}
        >
            <div className={"frame-name"}>
                {frameInfo.frameName}{" "}
                <div className={"frame-info"}>
                    <div className={"frame-cost"}>+{toLocalNumber(frame.frameCost)}€</div>
                </div>
            </div>
        </li>
    );
};

const FrameMaterial = (props: FrameMaterialProps) => {
    const id = useChair((state) => state.chair.id);
    const defaultOption: ChairFrameOptions = { frames: [] };

    const frameOptionsQuery = useListChairFrameOptions({ id: id });

    const frameOptions = frameOptionsQuery.isSuccess
        ? (frameOptionsQuery.data.chairEntries[0] ?? defaultOption)
        : defaultOption;

    return (
        <>
            <BreadCrumbs setCoverStep={props.setFrameStep} current={FrameBreadCrumb.MATERIAL} />
            <h2 className={"heading"}>Wählen Sie Ihr Gestell</h2>
            {frameOptionsQuery.isLoading ? (
                <Loader />
            ) : (
                <>
                    <h3>Material</h3>
                    <ul className={"frame-material-options"}>
                        {frameOptions.frames.length > 0
                            ? frameOptions.frames.map((frame) => {
                                  return (
                                      <FrameMaterialOption
                                          key={frame.id}
                                          frame={frame}
                                          setFrameStep={props.setFrameStep}
                                          setFinishings={props.setFinishings}
                                      />
                                  );
                              })
                            : "Keine Materialien verfügbar"}
                    </ul>
                </>
            )}
        </>
    );
};

const FinishingOption = (props: FinishingOptionProps) => {
    const id = useChair((state) => state.chair.id);
    const frame = useChair((state) => state.chair.frame);
    const setChair = useChair((state) => state.setChair);
    const texture = props.finishing.texture.length > 0 ? props.finishing.texture[0] : null;

    const handleClick = (event) => {
        event.stopPropagation();

        if (props.finishing.textureName !== frame.texture?.name) {
            setChair({
                id: id,
                frame: {
                    ...frame,
                    texture: {
                        name: props.finishing.textureName,
                        shininess: props.finishing.shininess,
                        cost: props.finishing.cost,
                        id: props.finishing.texture[0]?.id,
                    },
                },
            });
        }
        plausibleEvent("finishing-selected", props.finishing.textureName);
    };
    return (
        <li className={"finishing-option"} onClick={handleClick}>
            <img
                className={"finishing-preview"}
                src={texture.url}
                width={50}
                height={50}
                alt={props.finishing.textureName}
            />
            <div className={"finishing-name"}>
                <div className={"name"}>{props.finishing.textureName}</div>
                <div className={"finishing-info"}>
                    <div className={"finishing-description"}>
                        {props.finishing.description ? (
                            <>
                                {props.finishing.description} <br />
                            </>
                        ) : null}
                        +{toLocalNumber(props.finishing.cost)}€
                    </div>
                    <div className={"finishing-cost"}></div>
                </div>
            </div>
            <input
                type={"radio"}
                checked={frame?.texture?.name === props.finishing.textureName}
                onChange={handleClick}
            />
        </li>
    );
};

const FrameFinishing = (props: FrameFinishingProps) => {
    return (
        <>
            <BreadCrumbs setCoverStep={props.setFrameStep} current={FrameBreadCrumb.FINISHING} />
            <h2 className={"heading"}>Wählen Sie eine Verarbeitung</h2>
            {props.finishings.length > 0 ? (
                <ul className={"finishing-options"}>
                    {props.finishings.map((finishing) => {
                        return <FinishingOption key={finishing.textureName} finishing={finishing} />;
                    })}
                </ul>
            ) : null}
        </>
    );
};

export const FrameStep = () => {
    const { setStep } = useStep();
    const [frameStep, setFrameStep] = useState<FrameBreadCrumb>(FrameBreadCrumb.MATERIAL);
    const [finishings, setFinishings] = useState<Array<Texture>>([]);

    const componentToBreadCrumb: Map<FrameBreadCrumb, JSX.Element> = new Map([
        [FrameBreadCrumb.MATERIAL, <FrameMaterial setFrameStep={setFrameStep} setFinishings={setFinishings} />],
        [FrameBreadCrumb.FINISHING, <FrameFinishing setFrameStep={setFrameStep} finishings={finishings} />],
    ]);

    return (
        <div className={"step frame-step"}>
            <button
                className={"button back-button icon-button"}
                onClick={() => {
                    setStep(Step.OVERVIEW);
                }}
            >
                Zurück
            </button>
            <ChairInfoButton />
            {componentToBreadCrumb.get(frameStep)}
        </div>
    );
};
